import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Clear, Done } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import { Alert, Box, Button, Container, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { GridColDef, GridRowModes, GridRowModesModel, useGridApiRef } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import UserName from '~/components/Common/UserName';

import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import { PromoRole } from '~/services/PromoService';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import Spinner from '../../Common/Spinner';
import DebugModal from '../../DebugModal/DebugModal';
import Table from '../../v2/common/Table/Table';
import usePromoAdminPermission from '../hooks/usePromoPermission';
import { usePromoRoles } from '../hooks/usePromoRoles';

import AddPromoUserToRoleForm from './AddPromoUserToRoleForm';
import CurrentUserRoleSummary from './CurrentUserRoleSummary';
import PromoRoleAdd from './PromoRoleAdd';
import PromoRoleUserAssignedList from './PromoRoleUserAssignedList';

// Type for row with delete action
type PromoRoleWithAction = PromoRole & { _action?: 'delete' };

const getColumns = (
  roles: Array<PromoRole>,
  currentUserId: string,
  handleUnassignRoleUser: (promoRoleId: string, userId: string) => void,
  handleInactiveToggle: (promoRoleId: string) => void,
  handleEditClick: (id: string) => void,
  handleSaveClick: (id: string) => void,
  handleCancelClick: (id: string) => void,
  handleDeleteClick: (id: string) => void,
  handleAssignUserToRole: (promoRoleId: string, userId: string) => void,
  rowModesModel: GridRowModesModel,
): Array<GridColDef<PromoRole>> => [
  {
    field: 'roleName',
    headerName: 'Name',
    flex: 2,
    editable: true,
    renderCell: (params) => (
      <Stack direction="row">
        {params.row.isSystem && (
          <Typography title="system promo roles - editing disallowed">
            <LockIcon />
          </Typography>
        )}
        <CopyableField value={params.row.roleName} />
      </Stack>
    ),
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 2,
    editable: true,
    renderCell: (params) => {
      return <Typography variant="body2">{params.row.description}</Typography>;
    },
  },
  {
    field: 'users',
    headerName: 'Assigned Users',
    flex: 3,
    renderCell: (params) => (
      <PromoRoleUserAssignedList
        role={params.row}
        roles={roles}
        currentUserId={currentUserId}
        addPromoUserToRole={handleAssignUserToRole}
        handleUnassignRoleUser={handleUnassignRoleUser}
        renderExtended={true}
        isEditMode={rowModesModel[params.row.roleId]?.mode === GridRowModes.Edit}
      />
    ),
  },
  {
    field: 'isActive',
    headerName: 'Active',
    flex: 1,
    renderCell: (params) => {
      const isInEditMode = rowModesModel[params.row.roleId]?.mode === GridRowModes.Edit;

      if (!isInEditMode) {
        return params.row.isActive ? <Done /> : <Clear />;
      }

      return (
        <Switch
          checked={params.row.isActive}
          size="small"
          onChange={() => handleInactiveToggle(params.row.roleId)}
          color={params.row.isActive ? 'success' : 'default'}
        />
      );
    },
  },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 2,
    renderCell: (params) => {
      const isInEditMode = rowModesModel[params.row.roleId]?.mode === GridRowModes.Edit;

      if (isInEditMode) {
        return (
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" onClick={() => handleSaveClick(params.row.roleId)}>
              Save
            </Button>
            <Button variant="outlined" size="small" onClick={() => handleCancelClick(params.row.roleId)}>
              Cancel
            </Button>

            <Button variant="outlined" size="small" onClick={() => handleDeleteClick(params.row.roleId)}>
              Delete
            </Button>
          </Stack>
        );
      }

      const details = (
        <>
          <Stack direction="column" spacing={0.5}>
            <Typography variant="caption">
              Created by <UserName userId={params.row.createdBy} />{' '}
              {params.row.modifiedBy && params.row.modifiedBy != params.row.createdBy && (
                <>
                  Modified by <UserName userId={params.row.modifiedBy} />{' '}
                </>
              )}
              on <DateFormatterDetailed date={params.row.updatedAt} />
            </Typography>
          </Stack>
        </>
      );

      if (params?.row?.isSystem) {
        return details;
      }

      return (
        <Stack direction="row">
          {details}
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleEditClick(params.row.roleId)}
          />
        </Stack>
      );
    },
  },
  {
    field: 'debug',
    headerName: 'Debug',
    editable: false,
    renderCell: (params) => <DebugModal title="Debug" type="generic" data={{ ...params.row }} />,
  },
];

const PAGE_FILTER = {
  page: 1,
  limit: 9999,
};

function PromoRoles() {
  const query = useQuery();
  const [includeInactive, setIncludeInactive] = useState<boolean>(query.get('includeInactive') === 'true' || false);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const showConfirmDialog = useConfirmDialog();
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useCurrentUser();

  const {
    promoRoles,
    isLoading,
    error,
    createPromoRole,
    fetchPromoRoles,
    updatePromoRole,
    assignUserToRole,
    unassignPromoRole,
    deletePromoRole,
  } = usePromoRoles({
    initialFilters: {
      ...PAGE_FILTER,
      includeInactive: includeInactive,
      extendedResponse: true,
    },
  });

  useEffect(() => {
    fetchPromoRoles({
      ...PAGE_FILTER,
      includeInactive: includeInactive,
      extendedResponse: true,
    });
  }, [fetchPromoRoles, includeInactive]);

  const isPromoAdmin = usePromoAdminPermission();

  const handleInactiveToggle = async (promoRoleId: string) => {
    try {
      const currentRole = promoRoles.find((role) => role.roleId === promoRoleId);
      if (!currentRole) {
        console.error('Role not found for toggle');
        return;
      }

      const newIsActive = !currentRole.isActive;
      await updatePromoRole(promoRoleId, { isActive: newIsActive });

      // Reset all rows to view mode
      setRowModesModel({});

      // If we're hiding inactive roles and the role is being deactivated,
      // use the _action: 'delete' mechanism for better performance
      if (!includeInactive && currentRole.isActive && !newIsActive) {
        // Use the Data Grid's updateRows method to remove the row
        if (apiRef.current) {
          apiRef.current.updateRows([{ id: promoRoleId, _action: 'delete' }]);
        }
        return;
      }

      fetchPromoRoles({
        ...PAGE_FILTER,
        includeInactive: includeInactive,
        extendedResponse: true,
      });
    } catch (error) {
      enqueueSnackbar(`Error updating promo role toggle ${error.message}`, { variant: 'error' });
    }
  };

  const handleUnassignRoleUser = async (roleId: string, userId: string) => {
    await unassignPromoRole(roleId, userId);
    fetchPromoRoles({
      ...PAGE_FILTER,
      includeInactive: includeInactive,
      extendedResponse: true,
    });
  };

  const handleAssignUserToRole = async (roleId: string, userId: string) => {
    await assignUserToRole(roleId, userId);
    fetchPromoRoles({
      ...PAGE_FILTER,
      includeInactive: includeInactive,
      extendedResponse: true,
    });
  };

  const handleEditClick = (id: string) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    fetchPromoRoles({
      ...PAGE_FILTER,
      includeInactive: includeInactive,
      extendedResponse: true,
    });
  };

  const handleDeleteClick = async (id: string) => {
    if (promoRoles.find((role) => role.roleId === id)?.isSystem) {
      enqueueSnackbar('Cannot delete system promo roles', { variant: 'error' });
      return;
    }
    if (promoRoles.find((role) => role.roleId === id)?.users?.length > 0) {
      enqueueSnackbar('Cannot delete promo roles with assigned users. Unassign all users to continue.', {
        variant: 'error',
      });
      return;
    }
    const confirmed = await showConfirmDialog({
      title: 'Delete Promo Role?',
      description: `This will delete the promo role, are you sure you want to proceed?`,
    });
    if (!confirmed) {
      return;
    }
    await deletePromoRole(id);

    // Clean up the rowModesModel by removing the deleted row
    const { [id]: deletedRow, ...remainingRows } = rowModesModel;
    setRowModesModel(remainingRows);

    fetchPromoRoles({
      ...PAGE_FILTER,
      includeInactive: includeInactive,
      extendedResponse: true,
    });
  };

  const handleCancelClick = (id: string) => {
    // Reset all rows to view mode
    setRowModesModel({});
  };

  const handleProcessRowUpdate = async (newRow: PromoRole, oldRow: PromoRole): Promise<PromoRoleWithAction> => {
    const updatedRole = { ...newRow };
    await updatePromoRole(updatedRole.roleId, updatedRole);

    // Reset all rows to view mode
    setRowModesModel({});

    // If the role was deactivated and we're not showing inactive roles,
    // return the row with _action: 'delete' to remove it from the grid
    if (!includeInactive && oldRow.isActive && !updatedRole.isActive) {
      return { ...updatedRole, _action: 'delete' };
    }

    // Otherwise, fetch fresh data and return the updated role
    fetchPromoRoles({
      ...PAGE_FILTER,
      includeInactive: includeInactive,
      extendedResponse: true,
    });
    return updatedRole;
  };

  const handleSaveClick = async (roleId: string) => {
    if (!apiRef.current) {
      console.error('Grid API not available');
      return;
    }

    try {
      // Get the original row data
      const originalRow = promoRoles.find((role) => role.roleId === roleId);

      if (!originalRow) {
        console.error('Original row not found');
        return;
      }

      // Stop the row edit mode to commit the changes
      apiRef.current.stopRowEditMode({ id: roleId, ignoreModifications: false });

      // Get the updated row data from the grid
      const updatedRow = apiRef.current.getRow(roleId);

      if (updatedRow) {
        // Process the update with the updated row data
        await handleProcessRowUpdate(updatedRow as PromoRole, originalRow);
      }

      // Reset all rows to view mode (this is now handled in handleProcessRowUpdate)
    } catch (error) {
      enqueueSnackbar('Error saving promo role', { variant: 'error' });
    }
  };

  const handleCreateRole = async (role: PromoRole) => {
    await createPromoRole(role);
    fetchPromoRoles({
      ...PAGE_FILTER,
      includeInactive: includeInactive,
      extendedResponse: true,
    });
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!promoRoles) {
    return (
      <Typography variant="body2" color="text.secondary">
        No promo roles found (error? {error?.message})
      </Typography>
    );
  }

  return (
    <Container maxWidth="xl">
      <Stack direction="column" spacing={3} mb={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Promo Roles</Typography>
          <Typography variant="caption" color="textSecondary">
            Promo roles are used control access to specific functionality and filter the promo tags a user can set.
            Click the edit icon to modify existing roles{' '}
          </Typography>
        </Box>
        {currentUser && (
          <CurrentUserRoleSummary
            userId={currentUser.memberId}
            authRoles={currentUser.roles}
            availableRoles={promoRoles}
          />
        )}
        {isPromoAdmin && (
          <Stack spacing={2}>
            <AddPromoUserToRoleForm addPromoUserToRole={handleAssignUserToRole} promoRoles={promoRoles} />
            <PromoRoleAdd createPromoRole={handleCreateRole} />
          </Stack>
        )}
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} size="small" />
            }
            label="Show Inactive"
          />

          {!isPromoAdmin && <Alert severity="warning">Promo admin role required for updates</Alert>}
          {isPromoAdmin && (
            <Typography>
              <Done titleAccess="This account as the promo admin role and can create/update`" />
              promo-admin
            </Typography>
          )}
        </Stack>

        {error && (
          <Typography variant="body2" color="error" title={`Error: ${error?.message}`}>
            Error fetching promo roles: {error.message} [Do you have the promo-admin role?]
          </Typography>
        )}
        {promoRoles?.length > 0 && (
          <Table
            getRowId={(row) => row.roleId}
            columns={getColumns(
              promoRoles,
              currentUser?.memberId,
              handleUnassignRoleUser,
              handleInactiveToggle,
              handleEditClick,
              handleSaveClick,
              handleCancelClick,
              handleDeleteClick,
              handleAssignUserToRole,
              rowModesModel,
            )}
            rows={promoRoles}
            paginationMode="client"
            autoHeight
            dataGridProps={{
              getRowHeight: () => 'auto',
              rowModesModel,
              onRowModesModelChange: (newModel) => setRowModesModel(newModel),
              editMode: 'row',
              disableRowSelectionOnClick: true,
              initialState: {
                sorting: {
                  sortModel: [
                    {
                      field: 'isSystem',
                      sort: 'desc',
                    },
                  ],
                },
              },
              processRowUpdate: handleProcessRowUpdate,
              apiRef: apiRef,
            }}
          />
        )}
        {!isLoading && promoRoles.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {includeInactive ? 'No active promo roles found' : 'No promo roles found'}
          </Typography>
        )}
      </Stack>
    </Container>
  );
}

export default PromoRoles;
